import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BlobItem } from "@azure/storage-blob";
import { BlobStore } from "@/stores/BlobStore";
import { useBlobData } from "@/hooks/useBlobData";
import { LoadingState } from "./LoadingState";
import { ErrorState } from "./ErrorState";
import { KnowledgeBaseHeader } from "./KnowledgeBaseHeader";
import { BlobList } from "./BlobList";
import { BlobViewerDialog } from "./blob-viewer-dialog/BlobViewerDialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export function KnowledgeBaseSettings() {
  const { t } = useTranslation();
  const [selectedBlob, setSelectedBlob] = useState<BlobItem | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const {
    loading,
    error,
    uploading,
    deleteConfirmation,
    closeDeleteConfirmation,
    confirmDelete
  } = BlobStore();

  // This hook will automatically sync TanStack Query data with BlobStore
  useBlobData();

  const handleBlobView = (blob: BlobItem) => {
    setSelectedBlob(blob);
    setViewerOpen(true);
  };

  const handleCloseViewer = (open: boolean) => {
    setViewerOpen(open);
    if (!open) {
      setSelectedBlob(null);
    }
  };

  return (
    <div className="p-4">
      <KnowledgeBaseHeader />
      {uploading && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            {t("knowledgeBase.upload.uploading")}...
          </p>
        </div>
      )}
      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} />
      ) : (
        <BlobList
          onBlobView={handleBlobView}
        />
      )}

      <BlobViewerDialog
        open={viewerOpen}
        onOpenChange={handleCloseViewer}
        blob={selectedBlob}
      />

      <ConfirmDialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirmation();
        }}
        title={t("knowledgeBase.deleteConfirm.title")}
        description={t("knowledgeBase.deleteConfirm.message", {
          fileName: deleteConfirmation.blobToDelete?.name || ""
        })}
        confirmText={t("knowledgeBase.delete")}
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </div>
  );
}